import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Search, Lock, CheckCircle2, Calendar, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const STAGE_COLORS = {
  NOT_STARTED: { bg: '#94a3b820', text: '#94a3b8' },
  FIT_UP: { bg: '#3b82f620', text: '#3b82f6' },
  WELDING: { bg: '#f59e0b20', text: '#f59e0b' },
  NDT: { bg: '#143A6B20', text: '#143A6B' },
  PAINTING: { bg: '#ec489920', text: '#ec4899' },
  DISPATCHED: { bg: '#22c55e20', text: '#22c55e' },
  DISPATCH: { bg: '#22c55e20', text: '#22c55e' },
};

const STAGE_KEYS = ['fitUp', 'welding', 'ndt', 'painting', 'dispatch'];
const STAGE_LABELS = { fitUp: 'Fit-Up', welding: 'Welding', ndt: 'NDT', painting: 'Painting', dispatch: 'Dispatch' };

function formatIndian(num) {
  if (!num && num !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
}

export default function PWMFabProgress({ projectId, filters }) {
  const [spools, setSpools] = useState([]);
  const [stageReport, setStageReport] = useState(null);
  const [gateStatus, setGateStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { spoolNumber, stage }
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (filters.agUg) params.set('agUg', filters.agUg);
      if (filters.area) params.set('area', filters.area);
      if (search) params.set('lineId', search);
      const [spoolsRes, stageRes, gateRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pwm/spools?${params}`),
        api.get(`/v1/projects/${projectId}/pwm/fabrication/stage-report${filters.agUg ? `?agUg=${filters.agUg}` : ''}`),
        api.get(`/v1/projects/${projectId}/pwm/spools-gate-status`),
      ]);
      setSpools(spoolsRes.data?.spools || []);
      setStageReport(stageRes.data);
      setGateStatus(gateRes.data || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, filters, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageClick = (spoolNumber, stage) => {
    const gs = gateStatus[spoolNumber]?.stages?.[stage];
    if (gs?.isLocked) return; // Can't edit locked cells
    if (gs?.isComplete) return; // Already completed
    setEditingCell({ spoolNumber, stage });
    setEditDate(new Date().toISOString().slice(0, 10));
  };

  const handleSaveStage = async () => {
    if (!editingCell || !editDate) return;
    setSaving(true);
    try {
      await api.put(
        `/v1/projects/${projectId}/pwm/spool-stage-gate`,
        { date: editDate },
        { params: { spoolNumber: editingCell.spoolNumber, stage: editingCell.stage } }
      );
      toast.success(`${STAGE_LABELS[editingCell.stage]} recorded for ${editingCell.spoolNumber}`);
      setEditingCell(null);
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message || detail.error : detail;
      toast.error(msg || 'Gate enforcement blocked this update');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const renderStageCell = (spool, stageKey, allocPct) => {
    const sg = spool.stageGates || {};
    const gate = sg[stageKey] || {};
    const gs = gateStatus[spool.spoolNumber]?.stages?.[stageKey];
    const isEditing = editingCell?.spoolNumber === spool.spoolNumber && editingCell?.stage === stageKey;

    // RULE: If allocation < 100%, ALL stages are locked — no dates shown
    if (allocPct < 100) {
      const lockReason = gs?.lockReason || `Hard Allocation ${allocPct.toFixed(0)}% (need 100%)`;
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-0.5 cursor-not-allowed opacity-50"
                data-testid={`stage-locked-${spool.spoolNumber}-${stageKey}`}>
                <Lock className="w-3 h-3" style={{ color: '#ef4444' }} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px] max-w-[200px]">
              <p className="font-semibold text-red-400">{STAGE_LABELS[stageKey]} Locked</p>
              <p>{lockReason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // Allocation >= 100% — show actual stage status
    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
            className="text-[9px] px-1 py-0.5 rounded border w-24"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid={`stage-date-input-${spool.spoolNumber}-${stageKey}`} />
          <button onClick={handleSaveStage} disabled={saving}
            className="p-0.5 rounded" style={{ color: '#22c55e' }}
            data-testid={`stage-save-${spool.spoolNumber}-${stageKey}`}>
            <CheckCircle2 className="w-3 h-3" />
          </button>
          <button onClick={() => setEditingCell(null)} className="p-0.5 rounded" style={{ color: '#ef4444' }}>
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    const isComplete = gate?.isComplete || !!gate?.date;
    if (isComplete) {
      const d = gate.date;
      const display = typeof d === 'string' ? d.slice(5, 10) : d;
      return (
        <span style={{ color: '#22c55e' }} className="font-mono text-[9px]"
          data-testid={`stage-complete-${spool.spoolNumber}-${stageKey}`}>
          {display}
        </span>
      );
    }

    // Sequential gate lock (prior stage not complete)
    const isLocked = gs?.isLocked;
    if (isLocked) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-0.5 cursor-not-allowed opacity-50"
                data-testid={`stage-locked-${spool.spoolNumber}-${stageKey}`}>
                <Lock className="w-3 h-3" style={{ color: '#ef4444' }} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px] max-w-[200px]">
              <p className="font-semibold text-red-400">{STAGE_LABELS[stageKey]} Locked</p>
              <p>{gs?.lockReason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // Unlocked & not complete — clickable
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => handleStageClick(spool.spoolNumber, stageKey)}
              className="flex items-center justify-center w-full h-full cursor-pointer hover:opacity-70 transition-opacity"
              data-testid={`stage-edit-${spool.spoolNumber}-${stageKey}`}>
              <Calendar className="w-3 h-3" style={{ color: 'var(--text-disabled, #666)' }} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-[10px]">
            Click to record {STAGE_LABELS[stageKey]} date
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="space-y-3" data-testid="pwm-fab-progress">
      {/* Stage-Wise Summary */}
      {stageReport && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
          <div className="p-3" style={{ background: 'var(--card-bg-alt)', borderBottom: '1px solid var(--border-color)' }}>
            <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Stage-Wise Summary — Total: {formatIndian(stageReport.totalInchDia)} Inch-Dia, {stageReport.totalSpools} Spools</p>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--card-bg)' }}>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Stage</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Spools Done</th>
                <th className="p-2 text-right font-medium" style={{ color: '#3b82f6' }}>Inch-Dia Done</th>
                <th className="p-2 text-right font-medium" style={{ color: '#ef4444' }}>Inch-Dia Balance</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Progress</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {(stageReport.stages || []).map(s => (
                <tr key={s.stage} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
                  <td className="p-2 font-semibold" style={{ color: 'var(--text-primary)' }}>{s.stage}</td>
                  <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{s.spoolsDone.toLocaleString()} / {s.spoolsTotal.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono" style={{ color: '#3b82f6' }}>{formatIndian(s.inchDiaDone)}</td>
                  <td className="p-2 text-right font-mono" style={{ color: '#ef4444' }}>{formatIndian(s.inchDiaBalance)}</td>
                  <td className="p-2">
                    <div className="w-24 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                      <div className="h-full rounded-full" style={{ width: `${s.progressPct}%`, background: '#143A6B' }} />
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono font-bold" style={{ color: '#143A6B' }}>{s.progressPct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Gate Legend */}
      <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}
        data-testid="gate-legend">
        <span className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>Gate Legend:</span>
        <span className="flex items-center gap-1 text-[9px]"><Lock className="w-3 h-3" style={{ color: '#ef4444' }} /> Locked (gate not met)</span>
        <span className="flex items-center gap-1 text-[9px]"><Calendar className="w-3 h-3" style={{ color: '#666' }} /> Ready (click to record)</span>
        <span className="flex items-center gap-1 text-[9px]"><CheckCircle2 className="w-3 h-3" style={{ color: '#22c55e' }} /> Completed</span>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter by line ID..." data-testid="pwm-fab-search"
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
      </div>

      {/* Spool Grid */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-[10px]">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)', minWidth: 130 }}>Spool No.</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Drawing</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Line ID</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>AG/UG</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Area</th>
              <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>ID</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Alloc%</th>
              <th className="p-2 text-center font-medium" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>Fit-Up</th>
              <th className="p-2 text-center font-medium" style={{ color: '#f59e0b' }}>Welding</th>
              <th className="p-2 text-center font-medium" style={{ color: '#143A6B' }}>NDT</th>
              <th className="p-2 text-center font-medium" style={{ color: '#ec4899' }}>Painting</th>
              <th className="p-2 text-center font-medium" style={{ color: '#22c55e' }}>Dispatch</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>Stage</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Fab %</th>
            </tr>
          </thead>
          <tbody>
            {spools.filter(s => s.spoolType !== 'ERECTION').map(spool => {
              const allocPct = gateStatus[spool.spoolNumber]?.allocPct ?? 0;
              // When alloc < 100%: Stage = NOT_STARTED, Fab% = 0%
              const effectiveStage = allocPct >= 100 ? (spool.currentStage || 'NOT_STARTED') : 'NOT_STARTED';
              const effectiveFabPct = allocPct >= 100 ? (spool.fabricationProgressPct || 0) : 0;
              const stc = STAGE_COLORS[effectiveStage] || STAGE_COLORS.NOT_STARTED;
              return (
                <tr key={spool.spoolNumber} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }} data-testid={`fab-spool-${spool.spoolNumber}`}>
                  <td className="p-2 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{spool.spoolNumber}</td>
                  <td className="p-2 text-[9px]" style={{ color: 'var(--text-muted)' }}>{spool.drawingId?.slice(-8)}</td>
                  <td className="p-2 font-mono text-[9px]" style={{ color: 'var(--text-secondary)' }}>{spool.lineId}</td>
                  <td className="p-2 text-center" style={{ color: spool.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{spool.agUg}</td>
                  <td className="p-2" style={{ color: 'var(--text-muted)' }}>{spool.areaCode}</td>
                  <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{formatIndian(spool.spoolInchDia)}</td>
                  <td className="p-2 text-center font-mono text-[9px]" style={{ color: allocPct >= 100 ? '#22c55e' : allocPct > 0 ? '#f59e0b' : '#ef4444' }}
                    data-testid={`alloc-pct-${spool.spoolNumber}`}>
                    {allocPct.toFixed(0)}%
                  </td>
                  {STAGE_KEYS.map((stageKey, idx) => (
                    <td key={stageKey} className="p-2 text-center" style={{ borderLeft: idx === 0 ? '2px solid var(--border-color)' : undefined }}>
                      {renderStageCell(spool, stageKey, allocPct)}
                    </td>
                  ))}
                  <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: stc.bg, color: stc.text }}>
                      {effectiveStage.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="p-2 text-center font-mono font-bold" style={{ color: effectiveFabPct >= 80 ? '#22c55e' : effectiveFabPct > 0 ? '#143A6B' : 'var(--text-muted)' }}>
                    {effectiveFabPct.toFixed(0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{spools.filter(s => s.spoolType !== 'ERECTION').length} fabrication spools</p>
    </div>
  );
}
